import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { StatsChartProps } from '@ui/components/StatsCharts/StatsChart';
import { useTranslations } from '@ui/i18n';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { useMemo } from 'react';

import type { StatsMetricValue, StatsResponseType } from './types';

const endpoint = process.env.NEXT_PUBLIC_STATS_API ?? '';

type ResultType = {
  minerMoleEmployed: StatsMetricValue[];
  cronosCruiserEmployed: StatsMetricValue[];
  boomerSquadEmployed: StatsMetricValue[];
};

async function fetchMetrics(
  startTimestamp: number,
  endTimestamp: number,
): Promise<ResultType> {
  const response: AxiosResponse<StatsResponseType> = await axios.post(
    endpoint,
    {
      endpointUrl: 'insights/cronos/veno/query',
      timeRange: {
        start: startTimestamp.toString(),
        end: endTimestamp.toString(),
        step: 86400,
      },
      queries: [
        {
          eventsQuery: {
            resource: {
              name: 'nftStakeEvent',
              type: 'EVENTS',
            },
            alias: '',
            id: 'a',
            aggregation: {
              aggregateProperties: {
                type: 'CUMULATIVE_COUNT',
                propertyName: 'transaction_hash',
              },
            },
            selectorExpr: {
              logicExpr: {
                expressions: [
                  {
                    selector: {
                      key: 'tokenContract',
                      operator: 'EQ',
                      value: [
                        {
                          stringValue:
                            '0x06596eD89aC4609DE47A21aF7E36b38b2dF57C26',
                        },
                      ],
                    },
                  },
                ],
                operator: 'AND',
              },
            },
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'nftUnstakeEvent',
              type: 'EVENTS',
            },
            alias: '',
            id: 'b',
            aggregation: {
              aggregateProperties: {
                type: 'CUMULATIVE_COUNT',
                propertyName: 'transaction_hash',
              },
            },
            selectorExpr: {
              logicExpr: {
                expressions: [
                  {
                    selector: {
                      key: 'tokenContract',
                      operator: 'EQ',
                      value: [
                        {
                          stringValue:
                            '0x06596eD89aC4609DE47A21aF7E36b38b2dF57C26',
                        },
                      ],
                    },
                  },
                ],
                operator: 'AND',
              },
            },
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'nftStakeEvent',
              type: 'EVENTS',
            },
            alias: '',
            id: 'c',
            aggregation: {
              aggregateProperties: {
                type: 'CUMULATIVE_COUNT',
                propertyName: 'transaction_hash',
              },
            },
            selectorExpr: {
              logicExpr: {
                expressions: [
                  {
                    selector: {
                      key: 'tokenContract',
                      operator: 'EQ',
                      value: [
                        {
                          stringValue:
                            '0xd25358e2cAD3E1Fd165887569892A99fFFA674ac',
                        },
                      ],
                    },
                  },
                ],
                operator: 'AND',
              },
            },
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'nftUnstakeEvent',
              type: 'EVENTS',
            },
            alias: '',
            id: 'd',
            aggregation: {
              aggregateProperties: {
                type: 'CUMULATIVE_COUNT',
                propertyName: 'transaction_hash',
              },
            },
            selectorExpr: {
              logicExpr: {
                expressions: [
                  {
                    selector: {
                      key: 'tokenContract',
                      operator: 'EQ',
                      value: [
                        {
                          stringValue:
                            '0xd25358e2cAD3E1Fd165887569892A99fFFA674ac',
                        },
                      ],
                    },
                  },
                ],
                operator: 'AND',
              },
            },
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'StakeNftCall',
              type: 'EVENTS',
            },
            alias: '',
            id: 'e',
            aggregation: {
              aggregateProperties: {
                type: 'CUMULATIVE_COUNT',
                propertyName: 'transaction_hash',
              },
            },
            selectorExpr: {
              logicExpr: {
                expressions: [
                  {
                    selector: {
                      key: 'tokenContract',
                      operator: 'EQ',
                      value: [
                        {
                          stringValue:
                            '0xb650279e3d726B0c75C984cAA55341cB87A7F501',
                        },
                      ],
                    },
                  },
                ],
                operator: 'AND',
              },
            },
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
        {
          eventsQuery: {
            resource: {
              name: 'UnstakeNftCall',
              type: 'EVENTS',
            },
            alias: '',
            id: 'f',
            aggregation: {
              aggregateProperties: {
                type: 'CUMULATIVE_COUNT',
                propertyName: 'transaction_hash',
              },
            },
            selectorExpr: {
              logicExpr: {
                expressions: [
                  {
                    selector: {
                      key: 'tokenContract',
                      operator: 'EQ',
                      value: [
                        {
                          stringValue:
                            '0xb650279e3d726B0c75C984cAA55341cB87A7F501',
                        },
                      ],
                    },
                  },
                ],
                operator: 'AND',
              },
            },
            groupBy: [],
            limit: 0,
            functions: [],
            disabled: true,
          },
          dataSource: 'EVENTS',
          sourceName: '',
        },
      ],
      formulas: [
        {
          expression: 'a-b',
          alias: 'VVS Miner Mole',
          id: 'A',
          disabled: false,
          functions: [],
        },
        {
          expression: 'c-d',
          alias: 'Cronos Cruisers',
          id: 'B',
          disabled: false,
          functions: [],
        },
        {
          expression: 'e-f',
          alias: 'Boomer Squad',
          id: 'C',
          disabled: false,
          functions: [],
        },
      ],
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const results = response.data.results;

  if (results.length < 2) {
    console.error('invalid response from API for useStatsHelpersByCollection');
  }

  const minerMole =
    results.find((r) => r.id === 'A')?.matrix?.samples[0].values ?? [];
  const cronosCruiser =
    results.find((r) => r.id === 'B')?.matrix?.samples[0].values ?? [];
  const boomerSquad =
    results.find((r) => r.id === 'C')?.matrix?.samples[0].values ?? [];

  return {
    minerMoleEmployed: minerMole,
    cronosCruiserEmployed: cronosCruiser,
    boomerSquadEmployed: boomerSquad,
  };
}

export const useStatsHelpersByCollection = (
  startTimestamp: number,
  endTimestamp: number,
  options?: UseQueryOptions<ResultType>,
): StatsChartProps => {
  const t = useTranslations();
  const queryKey = [
    'useStatsHelpersByCollection',
    startTimestamp,
    endTimestamp,
  ];

  const result = useQuery({
    queryKey,
    queryFn: () => fetchMetrics(startTimestamp, endTimestamp),
    ...options,
  }).data;

  return useMemo(
    () => ({
      title: t('Total Helpers Employed by Collection'),
      values: {
        'VVS Miner Mole': result?.minerMoleEmployed,
        'Cronos Cruisers': result?.cronosCruiserEmployed,
        'Boomer Squad': result?.boomerSquadEmployed,
      },
      type: 'line',
    }),
    [result, t],
  );
};
